import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { tasksApi, taskStatusesApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";

export interface ChangeTaskStatusModalProps {
    isOpen: boolean;
    task: any | null;
    onClose: () => void;
    onSuccess: (updated: any) => void;
}

export const ChangeTaskStatusModal: React.FC<ChangeTaskStatusModalProps> = ({
    isOpen,
    task,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess } = useToast();
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<any[]>([]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const fetchStatuses = async () => {
            try {
                const res = await taskStatusesApi.list();
                const statusData = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.data)
                      ? res.data.data
                      : [];

                if (res?.result !== false) {
                    setStatuses(statusData);
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchStatuses();
    }, [isOpen]);

    useEffect(() => {
        if (task && task.status) {
            setSelectedStatus(String(task.status.id));
            setError(null);
        }
    }, [task]);

    const handleSubmit = async (): Promise<void> => {
        if (!task || !selectedStatus) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await tasksApi.update(task.id, {
                status_id: parseInt(selectedStatus, 10),
            });

            if (res.result) {
                showSuccess(t("tasks.modal_status.success"));
                const newStatusObj = statuses.find(
                    (s) => String(s.id) === selectedStatus
                );
                const updatedRow = {
                    ...task,
                    status: newStatusObj || task.status,
                };
                onSuccess(updatedRow);
                onClose();
            } else {
                setError(res.message || t("errors.default"));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    const statusOptions = statuses.map((s) => ({
        value: String(s.id),
        label: t(`tasks.statuses.${s.tag}`) || s.name,
    }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tasks.modal_status.title")}
            maxWidth="sm"
            onSubmit={handleSubmit}
            submitLabel={t("tasks.modal_status.save_button")}
            submittingLabel={t("tasks.modal_status.saving")}
            isSubmitting={loading}
            submitIcon={<Icon value="check" size={15} />}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 mb-3">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {task && (
                <div className="text-center mb-6">
                    <Badge value={task.code} />
                    <div className="mt-2 font-semibold">{task.title}</div>
                </div>
            )}

            <Select
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={statusOptions}
                className="w-full"
            />
        </Modal>
    );
};
